import { ItemGroup } from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/v2/empty-state";
import {
  useEntityActivity,
  type EntityActivityFilters,
} from "@/hooks/use-entity-activity";
import { ActivityRow } from "./activity-row";

type EntityActivityFeedProps = EntityActivityFilters & {
  // Copy varies by mount point. Default matches the most-common "no activity
  // yet" trigger-relationship empty state.
  emptyTitle?: string;
  emptyDescription?: string;
};

export function EntityActivityFeed({
  emptyTitle = "No messages or calls",
  emptyDescription = "Messages, calls, and notes for this record appear here once the first contact is made.",
  ...filters
}: EntityActivityFeedProps) {
  const { data, isLoading, isError } = useEntityActivity(
    filters as EntityActivityFilters,
  );

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        title="Couldn't load activity"
        description="Refresh to try again."
      />
    );
  }

  const rows = data?.activity ?? [];
  if (rows.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  // Read the full feed earliest → latest (chronological story), aligned with
  // the lifecycle rail beside it. The server returns newest-first (the right
  // default for the Overview preview, which is a separate component), so the
  // ascending order is applied here, in the full-feed view only — no shared
  // sort or other-entity page is affected. Sort on parsed epoch, not the raw
  // string: the three merged sources need not serialize timestamps identically.
  // Caveat: the server caps each load at `limit` (50) newest rows, so for a
  // feed with >50 events this shows the most-recent window oldest-first; true
  // full-history ascending lands with the server lifecycle endpoint (slice 2).
  const ordered = [...rows].sort(
    (a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at),
  );

  return (
    <ItemGroup className="gap-2">
      {ordered.map((r) => (
        // ADV-1345: `data-occurred-at` makes each row time-addressable for the
        // lifecycle scroll-spy. DOM order is ascending (matches `ordered`), so
        // the spy's "first row at/after a milestone" walk is monotonic.
        <ActivityRow key={r.id} row={r} data-occurred-at={r.occurred_at} />
      ))}
    </ItemGroup>
  );
}
